import React from "react";
import { createPortal } from "react-dom";
import { PORTAL_ABOVE_ALL_PANELS_Z } from "../../../utils/panelZIndex";
import { useFocusTrap } from "../../../hooks/useFocusTrap";

export function MobileAddConfirmOverlay({ mobileAddConfirm, onConfirm, onCancel }) {
  const dialogRef = React.useRef(null);
  useFocusTrap(dialogRef, Boolean(mobileAddConfirm));
  if (typeof document === "undefined" || !mobileAddConfirm) return null;
  return createPortal(
    <div
      className="fixed inset-0 flex flex-col items-center justify-center gap-4 bg-black/65 px-6 backdrop-blur-[5px] pointer-events-auto"
      style={{ zIndex: PORTAL_ABOVE_ALL_PANELS_Z }}
      onClick={onCancel}
      role="presentation"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="mobile-add-confirm-title"
        tabIndex={-1}
        className="flex max-w-[min(20rem,90vw)] flex-col items-center gap-4"
        onClick={(e) => e.stopPropagation()}
      >
        <p
          id="mobile-add-confirm-title"
          className="text-center text-sm font-medium text-gray-100 text-balance"
        >
          Do you want to add{" "}
          <span className="font-semibold text-white">{mobileAddConfirm.label}</span> to the scene?
        </p>
        <div className="flex w-full max-w-[min(20rem,90vw)] flex-col gap-2">
          <button
            type="button"
            onClick={onConfirm}
            className="w-full rounded-lg border border-white/25 bg-white/10 px-4 py-2 text-sm font-medium text-gray-100 active:bg-white/20"
          >
            Add
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="w-full rounded-lg border border-white/25 bg-white/10 px-4 py-2 text-sm font-medium text-gray-100 active:bg-white/20"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export function MobileSceneLoadOverlay({
  open,
  mobileLoadItemLabel,
  mobileLoadProgressPct,
  loadVerb = "Loading",
  anyLibraryCatalogInflight,
  onCancelCatalogLoad,
}) {
  if (typeof document === "undefined" || !open) return null;
  return createPortal(
    <div
      className="fixed inset-0 flex flex-col items-center justify-center gap-4 bg-black/65 px-6 backdrop-blur-[5px] pointer-events-auto"
      style={{ zIndex: PORTAL_ABOVE_ALL_PANELS_Z }}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <p className="text-center text-sm font-medium text-gray-100 text-balance max-w-[min(20rem,90vw)]">
        {mobileLoadItemLabel.trim() ? (
          <>
            {loadVerb} <span className="font-semibold text-white">{mobileLoadItemLabel}</span>...
          </>
        ) : (
          <>{loadVerb}...</>
        )}
      </p>
      {anyLibraryCatalogInflight ? (
        <button
          type="button"
          onClick={onCancelCatalogLoad}
          className="rounded-lg border border-white/25 bg-white/10 px-4 py-2 text-sm font-medium text-gray-100 active:bg-white/20"
          aria-label="Cancel loading"
        >
          Cancel
        </button>
      ) : null}
      <div
        className="relative h-2 w-[min(288px,85vw)] overflow-hidden rounded-full bg-gray-800/95 ring-1 ring-white/15"
        role="progressbar"
        aria-valuenow={mobileLoadProgressPct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Loading progress"
      >
        <div
          className="h-full rounded-full bg-white/95 shadow-[0_0_12px_rgba(255,255,255,0.35)] transition-[width] duration-150 ease-out"
          style={{ width: `${Math.min(100, Math.max(0, mobileLoadProgressPct))}%` }}
        />
      </div>
    </div>,
    document.body
  );
}

export function LoadingProgressBar({ progressPct, ariaLabel, className = "" }) {
  return (
    <div
      className={`relative h-2 overflow-hidden rounded-full bg-gray-800/95 ring-1 ring-white/15 ${className}`}
      role="progressbar"
      aria-valuenow={progressPct}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={ariaLabel}
    >
      <div
        className="h-full rounded-full bg-white/95 shadow-[0_0_12px_rgba(255,255,255,0.35)] transition-[width] duration-150 ease-out"
        style={{ width: `${Math.min(100, Math.max(0, progressPct))}%` }}
      />
    </div>
  );
}

export function DesktopSceneLoadToast({
  open,
  loadItemLabel,
  loadProgressPct,
  loadVerb = "Loading",
  anyLibraryCatalogInflight,
  onCancelCatalogLoad,
}) {
  if (typeof document === "undefined" || !open) return null;
  const label = String(loadItemLabel || "").trim();
  return createPortal(
    <div
      className="desktop-scene-load-toast pointer-events-auto rounded-md border border-gray-700 px-3 py-2 text-sm text-gray-100 shadow-lg backdrop-blur-sm"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="flex min-w-0 items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="truncate text-xs font-medium text-gray-100">
            {label ? `${loadVerb} ${label}...` : `${loadVerb}...`}
          </div>
          <LoadingProgressBar
            progressPct={loadProgressPct}
            ariaLabel="Scene loading progress"
            className="mt-2 w-full"
          />
        </div>
        {anyLibraryCatalogInflight ? (
          <button
            type="button"
            onClick={onCancelCatalogLoad}
            className="shrink-0 rounded border border-white/20 bg-white/10 px-2 py-1 text-xs font-medium text-gray-100 hover:bg-white/15 focus:outline-none focus-visible:ring-1 focus-visible:ring-gray-300"
            aria-label="Cancel loading"
          >
            Cancel
          </button>
        ) : null}
      </div>
    </div>,
    document.body
  );
}

export function SceneLoadHeaderProgress({ open, loadProgressPct }) {
  if (!open) return null;
  return (
    <span
      data-no-panel-drag="true"
      className="inline-flex w-24 shrink-0 items-center gap-1.5"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <LoadingProgressBar
        progressPct={loadProgressPct}
        ariaLabel="Library loading progress"
        className="h-1.5 flex-1"
      />
      <span className="min-w-7 text-right text-[10px] font-medium tabular-nums text-gray-300">
        {Math.round(Math.min(100, Math.max(0, loadProgressPct)))}%
      </span>
    </span>
  );
}
